'use client';

import React from 'react';
import { Star, Zap } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { POPULAR_ICONS } from '../blockHelpers';
import { BlockProps } from './types';
import { ResponsiveImage } from '../shared/ResponsiveImage';
import { Carousel, carouselPropsFromSettings } from '../shared/Carousel';

export interface TestimonialItem {
  quote: string;
  author: string;
  role?: string;
  avatar?: string;
  rating?: number;
}

export interface TrustBadgeItem {
  iconName: string;
  label: string;
}

const DEMO_REVIEWS: TestimonialItem[] = [
  { quote: 'Przykładowa opinia klienta — edytuj lub usuń w panelu bocznym.', author: 'Jan Kowalski', rating: 5 },
  { quote: 'Kolejna przykładowa opinia. Dodaj tu prawdziwe opinie swoich klientów.', author: 'Anna Nowak', rating: 5 },
];

export function TestimonialsBlock({ block, currentStyles, isAdmin, viewport }: BlockProps) {
  const settings = block.settings || {};
  const reviews: TestimonialItem[] = settings.reviews || [];
  const badges: TrustBadgeItem[] = settings.badges || [];
  const columns = settings.columns || 3;
  const columnsMobile = settings.columnsMobile || 1;
  const gapVal = currentStyles.gap !== undefined ? currentStyles.gap : settings.gap;
  const gap = gapVal !== undefined ? `${gapVal}px` : '20px';

  // Admin canvas simulates viewport by scaling a real-width div — Tailwind's md:/sm:/lg:
  // classes below key off the real browser window, not that scaled width, so they never
  // react to the viewport toggle here. Mirror the storefront's own breakpoints in JS instead
  // (sm:640/md:768/lg:1024, matching the canvas's tablet=768/mobile=375 simulated widths).
  const adminColumns = viewport === 'mobile' ? columnsMobile : viewport === 'tablet' ? (columns === 4 ? 2 : columns) : columns;

  const cardBg = currentStyles.cardBg || '#ffffff';
  const cardBorderColor = currentStyles.cardBorderColor || '#e2e8f0';
  const cardBorderRadius = currentStyles.cardBorderRadius !== undefined ? `${currentStyles.cardBorderRadius}px` : '16px';
  const quoteColor = currentStyles.quoteColor || '#334155';
  const authorColor = currentStyles.authorColor || '#0f172a';
  const starColor = currentStyles.starColor || '#f59e0b';
  const badgeColor = currentStyles.badgeColor || '#2563eb';
  const badgeLabelColor = currentStyles.badgeLabelColor || '#475569';
  const cardPadding = currentStyles.cardPadding !== undefined ? `${currentStyles.cardPadding}px` : undefined;
  // 'centered': large round photo above the quote, name and role centered below (portrait style).
  const centered = settings.cardStyle === 'centered';
  const avatarSize = Number(currentStyles.avatarSize ?? settings.avatarSize ?? (centered ? 120 : 32));
  const showQuotes = settings.showQuotes !== false;
  const quoteFontSize = currentStyles.quoteFontSize ? `${currentStyles.quoteFontSize}px` : undefined;
  const authorFontSize = currentStyles.authorFontSize ? `${currentStyles.authorFontSize}px` : undefined;
  const isCarousel = settings.layout === 'carousel';
  const carousel = carouselPropsFromSettings(settings, { desktop: columns, tablet: Math.min(columns, 2), mobile: columnsMobile });
  const gapPx = gapVal !== undefined ? Number(gapVal) : 20;

  const displayReviews = reviews.length > 0 ? reviews : (isAdmin ? DEMO_REVIEWS : []);
  const blockClass = `testimonials-${block.id}`;

  if (displayReviews.length === 0 && badges.length === 0 && !isAdmin) return null;

  return (
    <div className={cn('w-full', blockClass)}>
      <style>{`
        .${blockClass} .tst-card {
          background: ${cardBg};
          border: 1px solid ${cardBorderColor};
          border-radius: ${cardBorderRadius};
        }
        .${blockClass} .tst-quote { color: ${quoteColor}; }
        .${blockClass} .tst-author { color: ${authorColor}; }
        .${blockClass} .tst-star { color: ${starColor}; }
        .${blockClass} .tst-badge-icon { color: ${badgeColor}; }
        .${blockClass} .tst-badge-label { color: ${badgeLabelColor}; }
      `}</style>

      {isAdmin && reviews.length === 0 && (
        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-700 font-semibold">
          Podgląd — przykładowe opinie. Dodaj prawdziwe opinie klientów w panelu bocznym przed publikacją.
        </div>
      )}

      {displayReviews.length > 0 && (() => {
        const cards = displayReviews.map((review, i) => {
          const avatar = review.avatar ? (
            <div className="relative shrink-0 overflow-hidden rounded-full bg-gray-100" style={{ width: avatarSize, height: avatarSize }}>
              <ResponsiveImage src={review.avatar} alt="" sizes={`${avatarSize * 2}px`} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          ) : (
            <div className="rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-400 uppercase shrink-0" style={{ width: avatarSize, height: avatarSize, fontSize: Math.max(10, avatarSize / 3) }}>
              {review.author.slice(0, 1)}
            </div>
          );
          const stars = !!review.rating && (
            <div className={cn('flex gap-0.5', centered && 'justify-center')}>
              {Array.from({ length: 5 }).map((_, s) => (
                <Star key={s} className={cn('tst-star w-3.5 h-3.5', s < (review.rating || 0) ? 'fill-current' : 'opacity-25')} />
              ))}
            </div>
          );
          const quote = (
            <p className="tst-quote text-sm leading-relaxed" style={quoteFontSize ? { fontSize: quoteFontSize } : undefined}>
              {showQuotes ? <>&ldquo;{review.quote}&rdquo;</> : review.quote}
            </p>
          );
          return (
            <div
              key={i}
              className={cn('tst-card flex flex-col gap-3 h-full', centered && 'items-center text-center', !cardPadding && 'p-5')}
              style={cardPadding ? { padding: cardPadding } : undefined}
            >
              {centered ? (
                <>
                  {avatar}
                  {stars}
                  {quote}
                  <div className="mt-1">
                    <div className="tst-author text-xs font-bold uppercase tracking-[0.2em]" style={authorFontSize ? { fontSize: authorFontSize } : undefined}>{review.author}</div>
                    {review.role && <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-gray-400">{review.role}</div>}
                  </div>
                </>
              ) : (
                <>
                  {stars}
                  {quote}
                  <div className="flex items-center gap-2 mt-1">
                    {avatar}
                    <div>
                      <div className="tst-author text-xs font-bold" style={authorFontSize ? { fontSize: authorFontSize } : undefined}>{review.author}</div>
                      {review.role && <div className="text-[11px] text-gray-400">{review.role}</div>}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        });

        return isCarousel ? (
          <Carousel
            perView={carousel.perView}
            gap={gapPx}
            autoplayMs={carousel.autoplayMs}
            loop={carousel.loop}
            arrows={carousel.arrows}
            dots={carousel.dots}
            viewport={isAdmin ? viewport : undefined}
            isAdmin={isAdmin}
          >
            {cards}
          </Carousel>
        ) : (
          <div
            className={cn(
              'grid',
              !isAdmin && cn(
                columnsMobile === 2 ? 'grid-cols-2' : 'grid-cols-1',
                columns === 1 ? 'md:grid-cols-1' :
                columns === 2 ? 'md:grid-cols-2' :
                columns === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' :
                'md:grid-cols-3'
              )
            )}
            style={{ gap, ...(isAdmin ? { gridTemplateColumns: `repeat(${adminColumns}, 1fr)` } : {}) }}
          >
            {cards}
          </div>
        );
      })()}

      {badges.length > 0 && (
        <div className={cn('flex flex-wrap items-center gap-6', displayReviews.length > 0 && 'mt-8 pt-8 border-t border-gray-100')}>
          {badges.map((badge, i) => {
            const IconComponent = POPULAR_ICONS[badge.iconName] || Zap;
            return (
              <div key={i} className="flex items-center gap-2">
                <IconComponent className="tst-badge-icon w-5 h-5" strokeWidth={1.75} />
                <span className="tst-badge-label text-xs font-semibold">{badge.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
